import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { XCircleIcon } from "@phosphor-icons/react/dist/csr/XCircle";
import { useState } from "react";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { RadioGroup } from "@volca/ui/components/ui/radio-group";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { formatDateRange } from "@/core/utils/date";
import {
  getRerunScope,
  type RerunUnit,
} from "@/payroll/pay-breakdown/rerun-units";
import {
  AI_SCRIPT_RERUN_VERSION_CHOICE,
  type AiScriptRerunRunOptions,
  type AiScriptRerunSelection,
  type AiScriptRerunVersionChoice,
  type AiScriptRerunVersionInfo,
} from "@/payroll/pay-breakdown/types";
import {
  buildAiScriptRerunOptions,
  getAiScriptRerunState,
  getMissingAiScriptRerunVersionReferences,
} from "@/payroll/pay-breakdown/utils";
import { RerunInfoBanner } from "./rerun-info-banner";
import { RERUN_SCOPE, type RerunScope } from "./rerun-scope";
import { RerunUnitOption } from "./rerun-unit-option";
import { RerunVersionChoices } from "./rerun-version-choices";
import { ScopeOption } from "./scope-option";

interface AiScriptRerunConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (
    runOptions?: AiScriptRerunRunOptions,
    sheetNames?: string[],
  ) => void;
  periodStart: string | undefined;
  periodEnd: string | undefined;
  versionInfo: AiScriptRerunVersionInfo;
  /**
   * The groups of tabs that can be rerun on their own, in workbook order. Empty
   * for a business that hasn't adopted access keys, or whose workbook predates
   * sheet classification — the dialog then offers a whole-workbook rerun only.
   */
  units?: RerunUnit[];
  /** Whether this user may rerun the whole period, not just their own units. */
  canRerunEverything?: boolean;
  /**
   * Whether a run is already in flight for this period. Runs execute one at a
   * time, so this one waits for that one to finish before it starts — worth
   * saying, because the wait is then two full runs rather than one.
   */
  hasRunInProgress?: boolean;
  /**
   * A full-workbook AI run, or an ICM/legacy calculation, is already running.
   * It rewrites everything anyway, so picking single units is pointless while it
   * is in flight.
   */
  isBlockedByOtherRun?: boolean;
}

/**
 * Confirms a rerun and explains what it will do.
 *
 * One dialog for both scopes, reached from the single rerun button, because the
 * choice between "everything" and "just this department" is the thing that needs
 * explaining — and a control per tab could only ever ask half the question.
 *
 * Scope is offered by UNIT rather than by tab: the access key is already the
 * unit of sign-off (approval fingerprints span every sheet sharing a key), so
 * rerunning part of one withdraws the whole unit's approval regardless. Offering
 * the part would be offering a choice with no distinct outcome.
 */
export const AiScriptRerunConfirmationDialog = ({
  open,
  onOpenChange,
  ...contentProps
}: AiScriptRerunConfirmationDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[85vh] grid-rows-[auto_minmax(0,1fr)_auto]">
      <AiScriptRerunConfirmationDialogContent
        key={open ? "open" : "closed"}
        onOpenChange={onOpenChange}
        {...contentProps}
      />
    </DialogContent>
  </Dialog>
);

type AiScriptRerunConfirmationDialogContentProps = Omit<
  AiScriptRerunConfirmationDialogProps,
  "open"
>;

const AiScriptRerunConfirmationDialogContent = ({
  onOpenChange,
  onConfirm,
  periodStart,
  periodEnd,
  versionInfo,
  units = [],
  canRerunEverything = true,
  hasRunInProgress = false,
  isBlockedByOtherRun = false,
}: AiScriptRerunConfirmationDialogContentProps) => {
  const { scriptDrift, rosterDrift, hasDrift } =
    getAiScriptRerunState(versionInfo);

  // Units are only worth offering when there is a choice to make: with nothing
  // else runnable, "just this one" and "everything" are the same run.
  const offersUnits = units.length > 0 && !isBlockedByOtherRun;
  const offersScopeChoice = offersUnits && canRerunEverything;
  // Without whole-payroll authority the scope is ALWAYS a set of units, even
  // when there are none to pick: an empty scope means "the whole workbook" to
  // the API, so defaulting to it would be handing a department lead the one run
  // they are not allowed to start. With nothing selected the confirm stays
  // disabled, which is the correct dead end.
  const defaultScope: RerunScope = canRerunEverything
    ? RERUN_SCOPE.everything
    : RERUN_SCOPE.units;
  const [scope, setScope] = useState<RerunScope>(defaultScope);
  // An available unit is selected unless the user removes it. This also selects
  // units that arrive after the dialog opens without restoring units the user
  // already removed.
  const [deselectedUnitIds, setDeselectedUnitIds] = useState<string[]>([]);
  const [scriptChoice, setScriptChoice] =
    useState<AiScriptRerunVersionChoice | null>(
      defaultScope === RERUN_SCOPE.units && scriptDrift
        ? AI_SCRIPT_RERUN_VERSION_CHOICE.keep
        : null,
    );
  const [rosterChoice, setRosterChoice] =
    useState<AiScriptRerunVersionChoice | null>(null);

  // Re-derived from the live list every render, so a unit that started running
  // while the dialog sat open drops out of the scope instead of being submitted
  // for a second recalculation nobody asked for.
  const selectedUnits = units.filter(
    (unit) =>
      !deselectedUnitIds.includes(unit.id) && getRerunScope([unit]).length,
  );
  const scopedSheetNames =
    scope === RERUN_SCOPE.units ? getRerunScope(selectedUnits) : [];
  const isScopedRerun = scope === RERUN_SCOPE.units;
  const rerunSelection: AiScriptRerunSelection = {
    versionInfo,
    scriptChoice,
    rosterChoice,
    isScopedRerun,
  };
  const missingVersionReferences =
    getMissingAiScriptRerunVersionReferences(rerunSelection);
  const hasInvalidVersionReferences = missingVersionReferences.length > 0;

  const handleScopeChange = (next: string) => {
    const nextScope = next as RerunScope;
    setScope(nextScope);
    if (nextScope === RERUN_SCOPE.units && scriptDrift) {
      setScriptChoice(AI_SCRIPT_RERUN_VERSION_CHOICE.keep);
    }
  };

  const handleConfirm = () => {
    onOpenChange(false);
    onConfirm(buildAiScriptRerunOptions(rerunSelection), scopedSheetNames);
  };

  // Every drifted dimension must be answered before the re-run is enabled.
  const awaitingChoice =
    (scriptDrift && !isScopedRerun && scriptChoice === null) ||
    (rosterDrift && rosterChoice === null);
  const awaitingSelection =
    scope === RERUN_SCOPE.units && scopedSheetNames.length === 0;

  const periodLabel =
    periodStart && periodEnd
      ? ` for ${formatDateRange(periodStart, periodEnd)}`
      : "";

  return (
    <>
      <DialogHeader>
        <DialogTitle>Rerun payroll{periodLabel}?</DialogTitle>
        <DialogDescription className="sr-only">
          Confirm rerunning payroll for this period
        </DialogDescription>
      </DialogHeader>
      <Stack align="start" className="min-h-0 w-full overflow-y-auto" gap={6}>
        {hasDrift && (
          <Text tone="muted">
            The last run for this period used older payroll settings. Choose
            which versions to run with.
          </Text>
        )}

        {isBlockedByOtherRun && (
          <Alert color="warning" role="status">
            <WarningIcon />
            <AlertDescription>
              <Text variant="caption">
                A payroll run covering the whole period is already going. It
                recalculates every tab, so there is nothing to pick here.
              </Text>
            </AlertDescription>
          </Alert>
        )}

        {offersScopeChoice ? (
          <RadioGroup value={scope} onValueChange={handleScopeChange}>
            <Stack className="w-full" align="start" gap={4}>
              <ScopeOption
                value={RERUN_SCOPE.everything}
                title="Everything"
                detail="Runs payroll again for the whole period and replaces the Grid."
              />
              <ScopeOption
                value={RERUN_SCOPE.units}
                title="Just some of it"
                detail="Pick the groups to recalculate. Shared dependencies can also recalculate related sheets."
              />
            </Stack>
          </RadioGroup>
        ) : (
          !isBlockedByOtherRun && (
            <Text tone="muted">
              {offersUnits
                ? "Pick what to recalculate. Shared dependencies can also recalculate related sheets."
                : `This will run payroll again${periodLabel} and replace the current Grid.`}
            </Text>
          )
        )}

        {offersUnits && scope === RERUN_SCOPE.units && (
          <Stack className="w-full" align="start" gap={2}>
            {units.map((unit) => (
              <RerunUnitOption
                key={unit.id}
                unit={unit}
                checked={
                  !deselectedUnitIds.includes(unit.id) &&
                  getRerunScope([unit]).length > 0
                }
                onToggle={(checked) =>
                  setDeselectedUnitIds((current) =>
                    checked
                      ? current.filter((id) => id !== unit.id)
                      : current.includes(unit.id)
                        ? current
                        : [...current, unit.id],
                  )
                }
              />
            ))}
          </Stack>
        )}

        {hasInvalidVersionReferences && (
          <Alert color="danger">
            <XCircleIcon />
            <AlertDescription>
              <Text variant="caption">
                Payroll cannot be rerun because this period is missing its saved{" "}
                {missingVersionReferences.join(" and ")} version reference.
                Contact support before rerunning.
              </Text>
            </AlertDescription>
          </Alert>
        )}

        {hasDrift && (
          <RerunVersionChoices
            versionInfo={versionInfo}
            isScopedRerun={isScopedRerun}
            scriptChoice={scriptChoice}
            rosterChoice={rosterChoice}
            setScriptChoice={setScriptChoice}
            setRosterChoice={setRosterChoice}
          />
        )}

        <RerunInfoBanner
          canRerunEverything={canRerunEverything}
          hasRunInProgress={hasRunInProgress}
          isScopedRerun={isScopedRerun}
        />
      </Stack>
      <DialogFooter>
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={
            awaitingChoice ||
            hasInvalidVersionReferences ||
            awaitingSelection ||
            isBlockedByOtherRun
          }
        >
          {scope === RERUN_SCOPE.everything
            ? "Rerun payroll"
            : // Counts what would actually be submitted, so a unit that went
              // busy while the dialog sat open cannot leave this promising a
              // group it has already dropped.
              selectedUnits.length === 0
              ? "Rerun"
              : selectedUnits.length === 1
                ? `Rerun ${selectedUnits[0].name}`
                : `Rerun ${selectedUnits.length} groups`}
        </Button>
      </DialogFooter>
    </>
  );
};
